import {
  addEventParam,
  INinjaEvent,
  useQueuedEventStore,
} from '@ninjacart/nc-events-sdk-v2';
import { useFeatureFlag } from '@ninjacart/nc-nk-ff4j';
import {
  FallBackImage,
  KissanEventType,
  useAppPersistenceStore,
  useGlobalLoginContext,
  useProfileStore,
  useResumableFeedStore,
} from '@ninjacart/nc-nk-store';
import {
  Button,
  Card,
  Image,
  Paragraph,
  Text,
  View,
  XStack,
  YStack
} from '@ninjacart/nc-nk-ui';
import { useRouter } from '@ninjacart/nc-nk-web-deps';
import { HelpCircle, RefreshCcw, X } from '@tamagui/lucide-icons';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Animated, Easing } from 'react-native';
import { LinearGradient } from 'tamagui/linear-gradient';

export const AskQuestionCtaCard = () => {
  const [showProgressBar, setShowProgressBar] = useState(true);
  const { push } = useRouter();
  const progresBarConfig = useResumableFeedStore((state) => state.progresBarConfig);
  const setProgresBarConfig = useResumableFeedStore((state) => state.setProgresBarConfig);
  const userProfile = useProfileStore((state) => state.userProfile);
  const hasUserLoggedIn = useProfileStore((state) => state.hasUserLoggedIn);
  const { t } = useTranslation();
  const animation = useRef(new Animated.Value(0)).current;
  const stopBackgroundUplode = () => {
    setShowProgressBar(false);
    setProgresBarConfig({ ...progresBarConfig, isError: false, progressPercentage: 0 });
  };
  const uniqueDeviceId = useAppPersistenceStore((state) => state.deviceId);
  const enqueue = useRef(useQueuedEventStore((state) => state.enqueue)).current;
  const requestQnAFeatureFlow = useFeatureFlag(
    'requestQnAFeatureFlow',
    'UserDeviceId',
    uniqueDeviceId!
  );

  const [qnaNewFlow, setQnaNewFlow] = useState({
    name: requestQnAFeatureFlow ? '/askQuestionSearch' : '/qna/create',
    routeName: requestQnAFeatureFlow ? 'askQuestionSearch/index' : 'qna/create/index',
    translate: `${t('page.askQuestionCtaQnaPage')}`,
    description: 'QNA page',
  });
  const fields = useGlobalLoginContext(['openLoginSheet','previousPage', 'navigationParams']);
  const setNavigationParams = fields.navigationParams.set;

  const navigateToCreateQuestion = () => {
    const qnaAskQuestionClickEvent: INinjaEvent = {
      name: KissanEventType.QNA_ASKQUES_CLICK,
      screenDetails: { screen: 'qna page' },
      eventParams: [
        addEventParam('animatedAskQuestionTabClicked', 'Animated Ask questionTab clicked'),
      ],
    };
    enqueue(qnaAskQuestionClickEvent);
    if (!hasUserLoggedIn()) {
      fields['openLoginSheet'].set(true);
      fields['previousPage'].set(qnaNewFlow?.description);
      setNavigationParams({
        name: '',
        routeName: { name: qnaNewFlow?.routeName },
        description: qnaNewFlow?.translate,
        screenName: qnaNewFlow?.description,
      });
    } else {
      push(qnaNewFlow?.name);
    }
  };

  const retryUpload = () => {
    push('/qna/create');
  };

  useEffect(() => {
    const qnaPageOpenSuccessEvent: INinjaEvent = {
      name: KissanEventType.QNA_PAGEOPEN_SUCCESS,
      screenDetails: { screen: 'ask question page' },
    };
    enqueue(qnaPageOpenSuccessEvent);

    const impressionevent: INinjaEvent = {
      name: KissanEventType.QNA_BANNER_VIEWED_MORETHAN_ONE_SECOND,
      screenDetails: { screen: 'ask question page' },
    };
    enqueue(impressionevent);

  }, []);

  useEffect(() => {
    // Start indeterminate animation
    Animated.loop(
      Animated.timing(animation, {
        toValue: 1,
        duration: 2000,
        easing: Easing.linear,
        useNativeDriver: true,
      })
    ).start();
  }, [animation]);

  return (
    <>
      <YStack
        position="relative"
        paddingHorizontal={8}
        paddingVertical={10}
        marginTop={50}
        onPress={navigateToCreateQuestion}
      >
        <LinearGradient
          colors={['#FFD2D2', '#FEC09D']}
          start={{ x: 0, y: 0 }} // Top-left corner
          end={{ x: 1, y: 1 }} // Bottom-right corner
          style={{ flex: 1, borderRadius: 16, padding: 16 }}
        >
          <View position="absolute" right={0} bottom={0}>
            {/* <QnABanner /> */}
            <Image
              source={{
                uri: 'https://tecxsprts3.s3.amazonaws.com/1738147097515-anurag_sizeinc.png',
              }}
              style={{ width: 136, height: 177 }}
            />
          </View>
          <View>
            <YStack>
              <XStack>
                <Paragraph
                  width={'82%'}
                  fontSize={18}
                  color={'#000'}
                  fontWeight={'700'}
                  top={10}
                  zIndex={999}
                >
                  {t('page.yourAnswerIsMinsAway')}
                </Paragraph>
              </XStack>
              <XStack width={'75%'} marginTop={16}>
                <Paragraph fontSize={13} color={'#000'} fontWeight={'500'}>
                  {t('page.getAnswersAllYourQueriesFromOurTeamExperts')}
                </Paragraph>
              </XStack>
            </YStack>
            <YStack width={'100%'} flexDirection="row">
              <XStack width={'45%'}>
                <Button
                  backgroundColor={'#E8FBF2'}
                  theme="active"
                  fontWeight={'600'}
                  borderRadius="$3"
                  color={'$primary'}
                  width={'95%'}
                  marginTop={15}
                  fontSize={12}
                  borderColor={'$primary'}
                  borderWidth={1}
                  padding={5}
                  onPress={navigateToCreateQuestion}
                  testID='kisan_tId_createQuestion_bannerClick'
                >
                  <HelpCircle size={16} color={'#368665'} />
                  <Text fontSize={14} fontWeight={'600'} color={'#368665'}>
                    {t('page.askQuestion')}
                  </Text>
                </Button>
              </XStack>
              <XStack marginLeft={8} width={'55%'}>
                <Card backgroundColor={'#FBFFFDCC'} padding={12} bottom={3}>
                  <XStack flexDirection="row">
                    <Text color={'#000'} fontSize={12} fontWeight={'bold'}>
                      {t('page.anuragGupta')}
                    </Text>
                    <Text
                      padding={2}
                      fontSize={9}
                      marginLeft={2}
                      color={'#ffffff'}
                      fontWeight={'500'}
                      paddingHorizontal={4}
                      borderWidth={1}
                      borderRadius={25}
                      borderColor={'$primary'}
                      backgroundColor={'#368665'}
                    >
                      {' '}
                      {t('page.expert')}
                    </Text>
                  </XStack>
                  <YStack marginVertical={2}>
                    <Text color={'#000'} fontSize={10}>
                      {t('page.helpedFarmers')}
                    </Text>
                    <Text color={'#000'} fontSize={10}>
                      {t('page.yearsOfFarmingExperience')}
                    </Text>
                  </YStack>
                </Card>
              </XStack>
            </YStack>
          </View>
        </LinearGradient>
      </YStack>

      {(progresBarConfig?.progressPercentage > 0 || progresBarConfig.isError) &&
        showProgressBar &&
        !progresBarConfig.isSuccess && (
          <Card
            width={'100vw'}
            shadowColor={'#000000'}
            shadowRadius={5}
            elevation={5}
            margin={5}
            shadowOpacity={0.2}
            marginVertical={10}
            backgroundColor="$cardbackground"
            paddingLeft={15}
            paddingBottom={15}
            paddingTop={0}
            paddingRight={15}
          >
            <YStack height={72}>
              <XStack alignItems="center" gap={5} height={'95%'}>
                <Image source={{ uri: FallBackImage }} width={40} height={40} borderRadius={4} />
                <View style={{ width: '80%' }}>
                  <XStack alignItems="center" justifyContent={'space-between'} marginRight={15}>
                    {progresBarConfig.isError ? (
                      <Text
                        style={{
                          color: '#585858',
                          fontSize: 14,
                          fontWeight: '400',
                          paddingRight: 20,
                        }}
                      >
                        {t('page.somethingWentWrongPleaseTryAgainLater')} ...
                      </Text>
                    ) : (
                      <Text style={{ color: '#368665', fontSize: 13, fontWeight: '400' }}>
                        {t('page.weArePostingYourQuestion')} ...
                      </Text>
                    )}
                    {progresBarConfig.isError && (
                      <XStack>
                        <View onPress={retryUpload}
                        testID='kisan_tId_retryUpload_click'>
                          <RefreshCcw size={20} marginRight={5} />
                        </View>
                        <View onPress={stopBackgroundUplode}>
                          <X size={20} marginLeft={2} />
                        </View>
                      </XStack>
                    )}
                  </XStack>
                </View>
              </XStack>
              <View
                style={{
                  backgroundColor: progresBarConfig.isError ? '#FF0E0E' : '#CBE7DB',
                  height: 3.9,
                  display: 'flex',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                {Math.ceil(progresBarConfig.progressPercentage) < 100 ? (
                  <View
                    style={{
                      backgroundColor: progresBarConfig.isError ? '#FF0E0E' : '#368665',
                      height: 4,
                      width: `${Math.ceil(progresBarConfig.progressPercentage)}%`,
                      left: '0%',
                      position: 'relative',
                    }}
                  ></View>
                ) : (
                  <Animated.View
                    style={{
                      backgroundColor: 'green',
                      height: 4,
                      width: '100%',
                      transform: [
                        {
                          translateX: animation.interpolate({
                            inputRange: [0, 1],
                            outputRange: [-150, 150],
                          }),
                        },
                      ],
                    }}
                  />
                )}
              </View>
            </YStack>
          </Card>
        )}
    </>
  );
};
